import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  width?: string;
  disabled?: boolean;
  /** Show a filter input inside the dropdown (for long option lists). */
  searchable?: boolean;
  'aria-label'?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  width = '100%',
  disabled = false,
  searchable = false,
  'aria-label': ariaLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(q));
  }, [options, searchable, searchQuery]);

  // Close on click outside (dropdown is portal-positioned but stays in this subtree)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange, setIsOpen]);

  // Keyboard navigation (over the filtered list)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions, handleSelect]);

  // Reset highlight + search when opening; focus the filter input
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      const currentIndex = options.findIndex(opt => opt.value === value);
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
      if (searchable) {
        // After the dropdown renders
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }
  }, [isOpen, value, options, searchable]);

  // Keep the highlight valid as the filter narrows the list
  useEffect(() => {
    setHighlightedIndex(prev => (filteredOptions.length === 0 ? -1 : Math.min(Math.max(prev, 0), filteredOptions.length - 1)));
  }, [filteredOptions.length]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };

      updatePosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width }}
    >
      {/* Select button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`w-full h-8 px-3 text-[13px] font-medium rounded-lg border outline-none text-left transition-all flex items-center justify-between gap-2 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-100 ${
          disabled
            ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
            : selectedOption
              ? 'bg-white text-gray-900 border-gray-200 hover:border-gray-300 cursor-pointer'
              : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 cursor-pointer'
        } ${isOpen ? 'border-blue-500 ring-2 ring-blue-100' : ''}`}
      >
        <span className="flex-1 truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && dropdownPosition && (
        <>
          <style>{`
            @keyframes selectDropdownIn {
              from { opacity: 0; transform: translateY(-4px) scale(0.98); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
          <div
            ref={dropdownRef}
            role="listbox"
            className="fixed bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${Math.max(dropdownPosition.width, 120)}px`,
              maxHeight: '240px',
              zIndex: 10001,
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08)',
              animation: 'selectDropdownIn 150ms ease-out forwards'
            }}
          >
            {searchable && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-gray-100 flex-shrink-0">
                <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  aria-label="Filter options"
                  className="w-full text-[12px] font-medium text-gray-900 placeholder-gray-400 outline-none bg-transparent"
                />
              </div>
            )}
            <div className="p-1 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-2.5 py-2 text-[12px] text-gray-400">No matches</div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(option.value)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`flex items-center justify-between px-2.5 py-1.5 text-[12px] font-medium rounded-md cursor-pointer transition-colors ${
                        isHighlighted ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex-1 truncate">
                        {option.label}
                      </span>
                      {isSelected && (
                        <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
